// Content for the "Compare" tab on the Grammar screen.
//
// How it is organised:
// - Each IDEA is something you want to say (habits, happening now, past, future...), not a tense name.
// - Each idea lists the FORMS every language uses for that idea.
// - EXAMPLES are the same meaning in every language, so any pair of languages can be compared.
// - Explanations ({ en, it }) are shown in the interface language. Spanish, French and Portuguese
//   interface users see the English text until those translations are written.
// - To add a language later: add its code to COMPARE_LANGUAGES, then add its forms and its
//   example sentences (the key is the lowercase language name, like "spanish") to every idea.

import { BATCH_3 } from './grammarCompareBatch3';
import { BATCH_4 } from './grammarCompareBatch4';
import { BATCH_5 } from './grammarCompareBatch5';
import { FR_A1 } from './grammarCompareFrenchA1';
import { FR_A2A } from './grammarCompareFrenchA2a';
import { FR_A2B } from './grammarCompareFrenchA2b';
import { FR_A2C } from './grammarCompareFrenchA2c';
import { FR_B1A } from './grammarCompareFrenchB1a';
import { FR_B1B } from './grammarCompareFrenchB1b';
import { FR_B1C } from './grammarCompareFrenchB1c';
import { FR_B1D } from './grammarCompareFrenchB1d';
import { ES_A1A } from './grammarCompareSpanishA1a';
import { ES_A1B } from './grammarCompareSpanishA1b';
import { ES_A1C } from './grammarCompareSpanishA1c';
import { ES_A1D } from './grammarCompareSpanishA1d';
import { ES_A1E } from './grammarCompareSpanishA1e';
import { ES_A2A } from './grammarCompareSpanishA2a';
import { ES_A2B } from './grammarCompareSpanishA2b';
import { ES_A2C } from './grammarCompareSpanishA2c';
import { ES_A2D } from './grammarCompareSpanishA2d';
import { ES_A2E } from './grammarCompareSpanishA2e';
import { ES_A2F } from './grammarCompareSpanishA2f';
import { ES_B1A } from './grammarCompareSpanishB1a';
import { ES_B1B } from './grammarCompareSpanishB1b';
import { ES_B1C } from './grammarCompareSpanishB1c';
import { ES_B1D } from './grammarCompareSpanishB1d';
import { ES_B1E } from './grammarCompareSpanishB1e';
import { ES_B1F } from './grammarCompareSpanishB1f';
import { PT_A1A } from './grammarComparePortugueseA1a';
import { PT_A1B } from './grammarComparePortugueseA1b';
import { PT_A1C } from './grammarComparePortugueseA1c';
import { PT_A1D } from './grammarComparePortugueseA1d';
import { PT_A1E } from './grammarComparePortugueseA1e';
import { PT_A2A } from './grammarComparePortugueseA2a';
import { PT_A2B } from './grammarComparePortugueseA2b';
import { PT_A2C } from './grammarComparePortugueseA2c';
import { PT_A2D } from './grammarComparePortugueseA2d';
import { PT_A2E } from './grammarComparePortugueseA2e';
import { PT_A2F } from './grammarComparePortugueseA2f';
import { PT_B1A } from './grammarComparePortugueseB1a';
import { PT_B1B } from './grammarComparePortugueseB1b';
import { PT_B1C } from './grammarComparePortugueseB1c';
import { PT_B1D } from './grammarComparePortugueseB1d';

export const COMPARE_LANGUAGES = ['english', 'italian', 'french', 'spanish', 'portuguese'];

export const GRAMMAR_TAB_LABELS = {
    en: { verbs: 'Verbs', compare: 'Compare' },
    it: { verbs: 'Verbi', compare: 'Confronta' },
    es: { verbs: 'Verbos', compare: 'Comparar' },
    fr: { verbs: 'Verbes', compare: 'Comparer' },
    pt: { verbs: 'Verbos', compare: 'Comparar' },
};

const ALL_IDEAS = [
    {
        id: 'habits',
        level: 'A1',
        title: { en: 'Habits and routines', it: 'Abitudini e routine' },
        summary: {
            en: 'Things you do regularly, and facts that are always true.',
            it: 'Cose che fai regolarmente e fatti sempre veri.',
        },
        forms: {
            english: [
                {
                    name: 'Present simple',
                    formula: {
                        en: 'Subject + base verb. Add -s with he, she, it: she works.',
                        it: 'Soggetto + verbo base. Aggiungi -s con he, she, it: she works.',
                    },
                },
            ],
            italian: [
                {
                    name: 'Presente',
                    formula: {
                        en: 'The ending changes for each person: lavoro, lavori, lavora... The subject (io, tu...) is usually left out.',
                        it: 'La desinenza cambia per ogni persona: lavoro, lavori, lavora... Il soggetto (io, tu...) di solito si omette.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'I work at the bank.', tag: 'Present simple' },
                italian: { text: 'Lavoro alla banca.', tag: 'Presente' },
            },
            {
                english: { text: 'She drinks coffee every morning.', tag: 'Present simple' },
                italian: { text: 'Beve il caffè ogni mattina.', tag: 'Presente' },
            },
            {
                english: { text: 'We go to the gym on Mondays.', tag: 'Present simple' },
                italian: { text: 'Andiamo in palestra il lunedì.', tag: 'Presente' },
            },
            {
                english: { text: 'Water boils at 100 degrees.', tag: 'Present simple' },
                italian: { text: "L'acqua bolle a 100 gradi.", tag: 'Presente' },
            },
        ],
        notes: [
            {
                en: "English needs a helper verb (do / does) for questions and negatives: 'Do you work here?' Italian does not: 'Lavori qui?'",
                it: "L'inglese ha bisogno di un verbo ausiliare (do / does) per domande e negazioni: 'Do you work here?' L'italiano no: 'Lavori qui?'",
            },
            {
                en: "English always says the subject (I work). Italian usually drops it (lavoro), because the ending already shows who is acting. You can say 'Io lavoro...' to stress the person.",
                it: "L'inglese dice sempre il soggetto (I work). L'italiano di solito lo omette (lavoro), perché la desinenza mostra già chi agisce. Puoi dire 'Io lavoro...' per sottolineare la persona.",
            },
        ],
    },
    {
        id: 'now',
        level: 'A1',
        title: { en: 'Happening right now', it: 'Sta succedendo adesso' },
        summary: {
            en: 'Actions in progress at this moment.',
            it: 'Azioni in corso in questo momento.',
        },
        forms: {
            english: [
                {
                    name: 'Present continuous',
                    formula: {
                        en: 'am / is / are + verb-ing: I am working.',
                        it: 'am / is / are + verbo-ing: I am working.',
                    },
                },
            ],
            italian: [
                {
                    name: 'Presente',
                    formula: {
                        en: 'The simple presente is very common for things happening now: Adesso lavoro.',
                        it: 'Il presente semplice è molto comune per ciò che succede adesso: Adesso lavoro.',
                    },
                },
                {
                    name: 'Stare + gerundio',
                    formula: {
                        en: 'stare (sto, stai, sta...) + gerund. The gerund ends in -ando for -are verbs and -endo for -ere / -ire verbs: sto lavorando. It stresses that the action is in progress.',
                        it: 'stare (sto, stai, sta...) + gerundio. Il gerundio finisce in -ando per i verbi in -are e in -endo per i verbi in -ere / -ire: sto lavorando. Sottolinea che l\'azione è in corso.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'I am working at the bank right now.', tag: 'Present continuous' },
                italian: { text: 'Adesso sto lavorando alla banca.', tag: 'Stare + gerundio' },
            },
            {
                english: { text: 'She is drinking coffee.', tag: 'Present continuous' },
                italian: { text: 'Sta bevendo il caffè.', tag: 'Stare + gerundio' },
            },
            {
                english: { text: 'What are you doing?', tag: 'Present continuous' },
                italian: { text: 'Cosa stai facendo?', tag: 'Stare + gerundio' },
            },
            {
                english: { text: 'Be quiet, the baby is sleeping!', tag: 'Present continuous' },
                italian: { text: 'Silenzio, il bambino dorme!', tag: 'Presente' },
            },
        ],
        notes: [
            {
                en: "In English you normally need the continuous for what is happening now: 'I work now' sounds wrong. In Italian the simple presente is also correct, and stare + gerundio adds emphasis.",
                it: "In inglese per ciò che succede adesso serve di solito il continuous: 'I work now' suona sbagliato. In italiano anche il presente semplice è corretto, e stare + gerundio aggiunge enfasi.",
            },
            {
                en: 'Some irregular gerunds to remember: fare becomes facendo, bere becomes bevendo, dire becomes dicendo.',
                it: 'Alcuni gerundi irregolari da ricordare: fare diventa facendo, bere diventa bevendo, dire diventa dicendo.',
            },
        ],
    },
    {
        id: 'past-finished',
        level: 'A2',
        title: { en: 'Finished in the past', it: 'Finito nel passato' },
        summary: {
            en: 'Actions that started and ended in the past.',
            it: 'Azioni che sono iniziate e finite nel passato.',
        },
        forms: {
            english: [
                {
                    name: 'Past simple',
                    formula: {
                        en: 'Regular verbs add -ed: worked. Irregular verbs have their own form: went, ate, saw. The form is the same for every person.',
                        it: 'I verbi regolari aggiungono -ed: worked. I verbi irregolari hanno una forma propria: went, ate, saw. La forma è uguale per tutte le persone.',
                    },
                },
            ],
            italian: [
                {
                    name: 'Passato prossimo',
                    formula: {
                        en: 'avere or essere (in the presente) + past participle: ho lavorato, sono andato/a. The most common past tense in everyday speech.',
                        it: 'avere o essere (al presente) + participio passato: ho lavorato, sono andato/a. È il passato più usato nel parlato di ogni giorno.',
                    },
                },
                {
                    name: 'Passato remoto',
                    formula: {
                        en: 'One-word past: lavorai, andai, scrisse. Mostly for history and storytelling in writing; in speech it depends on the region.',
                        it: 'Passato in una sola parola: lavorai, andai, scrisse. Si usa soprattutto per la storia e le narrazioni scritte; nel parlato dipende dalla regione.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'I worked at the bank yesterday.', tag: 'Past simple' },
                italian: { text: 'Ieri ho lavorato alla banca.', tag: 'Passato prossimo' },
            },
            {
                english: { text: 'She went to Rome last week.', tag: 'Past simple' },
                italian: { text: 'La settimana scorsa è andata a Roma.', tag: 'Passato prossimo' },
            },
            {
                english: { text: 'We ate pizza on Saturday.', tag: 'Past simple' },
                italian: { text: 'Sabato abbiamo mangiato la pizza.', tag: 'Passato prossimo' },
            },
            {
                english: { text: 'Dante wrote the Divine Comedy.', tag: 'Past simple' },
                italian: { text: 'Dante scrisse la Divina Commedia.', tag: 'Passato remoto' },
            },
        ],
        notes: [
            {
                en: "Italian passato prossimo has two parts. Most verbs use avere. Verbs of movement and change (andare, venire, arrivare, nascere) and reflexive verbs use essere, and then the participle agrees with the subject: lei è andata, loro sono andati.",
                it: "Il passato prossimo ha due parti. La maggior parte dei verbi usa avere. I verbi di movimento e di cambiamento (andare, venire, arrivare, nascere) e i verbi riflessivi usano essere, e allora il participio concorda con il soggetto: lei è andata, loro sono andati.",
            },
            {
                en: "The passato prossimo also covers what English says with the present perfect: 'Ho mangiato' can mean 'I ate' or 'I have eaten'.",
                it: "Il passato prossimo copre anche ciò che l'inglese dice con il present perfect: 'Ho mangiato' può voler dire 'I ate' o 'I have eaten'.",
            },
        ],
    },
    {
        id: 'past-ongoing',
        level: 'A2',
        title: { en: 'Ongoing and habitual in the past', it: 'In corso e abituale nel passato' },
        summary: {
            en: 'The background of a past scene, actions in progress, and things you used to do.',
            it: 'Lo sfondo di una scena passata, azioni in corso e cose che facevi di solito.',
        },
        forms: {
            english: [
                {
                    name: 'Past continuous',
                    formula: {
                        en: 'was / were + verb-ing: I was working.',
                        it: 'was / were + verbo-ing: I was working.',
                    },
                },
                {
                    name: 'Used to',
                    formula: {
                        en: 'used to + base verb: I used to work there. Plain past simple with a time word (always, every day) also works.',
                        it: 'used to + verbo base: I used to work there. Anche il past simple con una parola di tempo (always, every day) funziona.',
                    },
                },
            ],
            italian: [
                {
                    name: 'Imperfetto',
                    formula: {
                        en: 'Stem + -avo, -avi, -ava, -avamo, -avate, -avano for -are verbs (lavoravo). -ere / -ire verbs use -evo / -ivo.',
                        it: 'Radice + -avo, -avi, -ava, -avamo, -avate, -avano per i verbi in -are (lavoravo). I verbi in -ere / -ire usano -evo / -ivo.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'I was working when you called.', tag: 'Past continuous' },
                italian: { text: 'Lavoravo quando hai chiamato.', tag: 'Imperfetto' },
            },
            {
                english: { text: 'When we were small, we used to play in the garden.', tag: 'Used to' },
                italian: { text: 'Quando eravamo piccoli, giocavamo in giardino.', tag: 'Imperfetto' },
            },
            {
                english: { text: 'It was raining and it was cold.', tag: 'Past continuous' },
                italian: { text: 'Pioveva e faceva freddo.', tag: 'Imperfetto' },
            },
            {
                english: { text: 'We always ate at my grandmother\'s house on Sundays.', tag: 'Past simple (habit)' },
                italian: { text: 'La domenica mangiavamo sempre a casa di mia nonna.', tag: 'Imperfetto' },
            },
        ],
        notes: [
            {
                en: 'One Italian tense does the job of several English ones. The imperfetto covers the past continuous, used to, and often the plain past simple for habits.',
                it: "Un solo tempo italiano fa il lavoro di più tempi inglesi. L'imperfetto copre il past continuous, used to e spesso il semplice past simple per le abitudini.",
            },
            {
                en: "Choosing between passato prossimo and imperfetto is a big step. The imperfetto sets the scene, and the passato prossimo is the event: 'Lavoravo (scene) quando hai chiamato (event)'.",
                it: "Scegliere tra passato prossimo e imperfetto è un passo importante. L'imperfetto crea la scena e il passato prossimo è l'evento: 'Lavoravo (scena) quando hai chiamato (evento)'.",
            },
        ],
    },
    {
        id: 'future',
        level: 'A2',
        title: { en: 'Future plans and predictions', it: 'Piani e previsioni per il futuro' },
        summary: {
            en: 'What you plan to do, have arranged, or think will happen.',
            it: 'Cosa hai in programma, hai organizzato o pensi che succederà.',
        },
        forms: {
            english: [
                {
                    name: 'Going to',
                    formula: {
                        en: 'am / is / are + going to + base verb: plans and intentions.',
                        it: 'am / is / are + going to + verbo base: piani e intenzioni.',
                    },
                },
                {
                    name: 'Present continuous',
                    formula: {
                        en: 'am / is / are + verb-ing: arrangements already made (I am meeting my boss tomorrow).',
                        it: 'am / is / are + verbo-ing: accordi già presi (I am meeting my boss tomorrow).',
                    },
                },
                {
                    name: 'Will',
                    formula: {
                        en: 'will + base verb: decisions made now, promises and predictions.',
                        it: 'will + verbo base: decisioni prese ora, promesse e previsioni.',
                    },
                },
            ],
            italian: [
                {
                    name: 'Presente',
                    formula: {
                        en: 'Very common for plans and arrangements, with a time word: Domani parto.',
                        it: 'Molto comune per piani e accordi, con una parola di tempo: Domani parto.',
                    },
                },
                {
                    name: 'Futuro semplice',
                    formula: {
                        en: 'Infinitive without the final -e, plus -ò, -ai, -à, -emo, -ete, -anno: lavorerò. For predictions, promises and plans.',
                        it: "Infinito senza la -e finale, più -ò, -ai, -à, -emo, -ete, -anno: lavorerò. Per previsioni, promesse e progetti.",
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'Next year I am going to work at the bank.', tag: 'Going to' },
                italian: { text: "L'anno prossimo lavorerò alla banca.", tag: 'Futuro semplice' },
            },
            {
                english: { text: 'I am meeting my boss tomorrow.', tag: 'Present continuous' },
                italian: { text: 'Domani incontro il mio capo.', tag: 'Presente' },
            },
            {
                english: { text: 'I will help you!', tag: 'Will' },
                italian: { text: 'Ti aiuterò!', tag: 'Futuro semplice' },
            },
            {
                english: { text: 'It will probably rain tomorrow.', tag: 'Will' },
                italian: { text: 'Domani probabilmente pioverà.', tag: 'Futuro semplice' },
            },
        ],
        notes: [
            {
                en: "English splits the future into three forms with different jobs. Italian often uses the plain presente for arranged plans ('Domani parto' = 'I am leaving tomorrow') and the futuro semplice for the rest.",
                it: "L'inglese divide il futuro in tre forme con compiti diversi. L'italiano usa spesso il presente semplice per i piani organizzati ('Domani parto' = 'I am leaving tomorrow') e il futuro semplice per il resto.",
            },
            {
                en: "Italian also uses the futuro to guess about the present: 'Sarà a casa' means 'He or she is probably at home'.",
                it: "L'italiano usa il futuro anche per fare ipotesi sul presente: 'Sarà a casa' vuol dire 'Probabilmente è a casa'.",
            },
        ],
    },
    {
        id: 'negation',
        level: 'A1',
        title: { en: 'Saying no (negation)', it: 'Dire di no (negazione)' },
        summary: {
            en: 'How to make a sentence negative.',
            it: 'Come rendere negativa una frase.',
        },
        forms: {
            english: [
                {
                    name: 'Do + not',
                    formula: {
                        en: "do / does / did + not + base verb: I do not work (I don't work). With be and modal verbs, add not after the verb: I am not, she cannot.",
                        it: "do / does / did + not + verbo base: I do not work (I don't work). Con be e i verbi modali, not va dopo il verbo: I am not, she cannot.",
                    },
                },
            ],
            italian: [
                {
                    name: 'Non + verbo',
                    formula: {
                        en: 'Put non directly before the verb: non lavoro. In the passato prossimo, non goes before avere / essere: non ho lavorato.',
                        it: 'Metti non subito prima del verbo: non lavoro. Nel passato prossimo, non va prima di avere / essere: non ho lavorato.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'I do not work at the bank.', tag: 'Do + not' },
                italian: { text: 'Non lavoro alla banca.', tag: 'Non + verbo' },
            },
            {
                english: { text: 'She does not drink coffee.', tag: 'Does + not' },
                italian: { text: 'Non beve il caffè.', tag: 'Non + verbo' },
            },
            {
                english: { text: 'We did not eat the pizza.', tag: 'Did + not' },
                italian: { text: 'Non abbiamo mangiato la pizza.', tag: 'Non + passato prossimo' },
            },
            {
                english: { text: "I don't see anything.", tag: 'One negative word' },
                italian: { text: 'Non vedo niente.', tag: 'Non + niente (double negative)' },
            },
        ],
        notes: [
            {
                en: 'Italian never uses a helper verb like do / does / did. Just put non before the verb.',
                it: "L'italiano non usa mai un verbo ausiliare come do / does / did. Basta mettere non prima del verbo.",
            },
            {
                en: "Italian uses double negatives and this is correct: 'Non vedo niente', 'Non viene mai'. English allows only one negative word: 'I don't see anything'.",
                it: "L'italiano usa la doppia negazione ed è corretto: 'Non vedo niente', 'Non viene mai'. L'inglese ammette una sola parola negativa: 'I don't see anything'.",
            },
            {
                en: "When nessuno comes before the verb, drop non: 'Nessuno mi ha chiamato'. After the verb you need it: 'Non mi ha chiamato nessuno'.",
                it: "Quando nessuno viene prima del verbo, togli non: 'Nessuno mi ha chiamato'. Dopo il verbo serve: 'Non mi ha chiamato nessuno'.",
            },
        ],
    },
    {
        id: 'questions',
        level: 'A1',
        title: { en: 'Asking questions', it: 'Fare domande' },
        summary: {
            en: 'Yes / no questions and questions with words like where, what and who.',
            it: 'Domande sì / no e domande con parole come dove, che cosa e chi.',
        },
        forms: {
            english: [
                {
                    name: 'Do + subject + verb',
                    formula: {
                        en: 'do / does / did + subject + base verb: Do you work here? With be and modal verbs, swap the subject and the verb: Are you tired? Can you help?',
                        it: 'do / does / did + soggetto + verbo base: Do you work here? Con be e i verbi modali, inverti soggetto e verbo: Are you tired? Can you help?',
                    },
                },
            ],
            italian: [
                {
                    name: 'Same order + ?',
                    formula: {
                        en: 'The word order is the same as in a statement. In speech the voice goes up at the end, in writing you add ?: Lavori qui? Question words go first: Dove lavori?',
                        it: "L'ordine delle parole è lo stesso di un'affermazione. Nel parlato la voce sale alla fine, nello scritto aggiungi ?: Lavori qui? Le parole interrogative vanno all'inizio: Dove lavori?",
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'Do you work at the bank?', tag: 'Do + subject + verb' },
                italian: { text: 'Lavori alla banca?', tag: 'Same order + ?' },
            },
            {
                english: { text: 'Where do you work?', tag: 'Question word + do' },
                italian: { text: 'Dove lavori?', tag: 'Question word + verb' },
            },
            {
                english: { text: 'Are you at home?', tag: 'Be + subject' },
                italian: { text: 'Sei a casa?', tag: 'Same order + ?' },
            },
            {
                english: { text: 'What does Maria want?', tag: 'Question word + does + subject' },
                italian: { text: 'Che cosa vuole Maria?', tag: 'Question word + verb + subject' },
            },
        ],
        notes: [
            {
                en: "There is no Italian word for 'do' or 'does' in a question. Do not translate it.",
                it: "In italiano non esiste una parola per 'do' o 'does' nelle domande. Non tradurla.",
            },
            {
                en: "When the subject is named, Italian usually puts it after the verb in a question word question: 'Dove lavora Maria?' (English: 'Where does Maria work?').",
                it: "Quando il soggetto è nominato, l'italiano di solito lo mette dopo il verbo nelle domande con una parola interrogativa: 'Dove lavora Maria?' (inglese: 'Where does Maria work?').",
            },
        ],
    },
    {
        id: 'articles',
        level: 'A1',
        title: { en: 'Articles (a, the)', it: 'Articoli (un, il)' },
        summary: {
            en: 'The small words before a noun.',
            it: 'Le piccole parole prima di un nome.',
        },
        forms: {
            english: [
                {
                    name: 'a / an / the',
                    formula: {
                        en: 'a before a consonant sound (a book), an before a vowel sound (an apple). the for a specific thing, singular or plural. No article for plurals or general ideas: Coffee is good.',
                        it: 'a prima di un suono consonantico (a book), an prima di un suono vocalico (an apple). the per una cosa specifica, al singolare o al plurale. Nessun articolo per i plurali o le idee generali: Coffee is good.',
                    },
                },
            ],
            italian: [
                {
                    name: 'Articolo determinativo',
                    formula: {
                        en: "Singular: il (il libro), lo (lo studente), l' (l'amico), la (la banca). Plural: i (i libri), gli (gli studenti, gli amici), le (le banche).",
                        it: "Singolare: il (il libro), lo (lo studente), l' (l'amico), la (la banca). Plurale: i (i libri), gli (gli studenti, gli amici), le (le banche).",
                    },
                },
                {
                    name: 'Articolo indeterminativo',
                    formula: {
                        en: "un (un libro), uno (uno studente), una (una banca), un' (un'amica).",
                        it: "un (un libro), uno (uno studente), una (una banca), un' (un'amica).",
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'The bank is open.', tag: 'the' },
                italian: { text: 'La banca è aperta.', tag: 'la (feminine)' },
            },
            {
                english: { text: 'I have a book.', tag: 'a' },
                italian: { text: 'Ho un libro.', tag: 'un (masculine)' },
            },
            {
                english: { text: 'The student is here.', tag: 'the' },
                italian: { text: 'Lo studente è qui.', tag: 'lo (before s + consonant)' },
            },
            {
                english: { text: 'The friends are arriving.', tag: 'the (plural)' },
                italian: { text: 'Gli amici arrivano.', tag: 'gli (plural, before a vowel)' },
            },
            {
                english: { text: 'Coffee is good.', tag: 'No article' },
                italian: { text: 'Il caffè è buono.', tag: 'il (article needed)' },
            },
        ],
        notes: [
            {
                en: 'Italian nouns are masculine or feminine, and the article has to match. English has one word, the, for everything.',
                it: "I nomi italiani sono maschili o femminili e l'articolo deve concordare. L'inglese ha una sola parola, the, per tutto.",
            },
            {
                en: "Italian uses the definite article for general ideas ('Il caffè è buono', 'Gli italiani amano il calcio'), and usually with possessives ('la mia casa'). English drops it in both cases.",
                it: "L'italiano usa l'articolo determinativo per le idee generali ('Il caffè è buono', 'Gli italiani amano il calcio') e di solito con i possessivi ('la mia casa'). L'inglese lo toglie in entrambi i casi.",
            },
        ],
    },
    {
        id: 'be-have-feel',
        level: 'A1',
        title: { en: 'Being, having and feeling', it: 'Essere, avere e stare' },
        summary: {
            en: 'Who you are, your age, how you feel. English uses to be for all of it; Italian shares the job between three verbs.',
            it: "Chi sei, la tua età, come ti senti. L'inglese usa to be per tutto; l'italiano divide il compito tra tre verbi.",
        },
        forms: {
            english: [
                {
                    name: 'To be',
                    formula: {
                        en: 'am / is / are for identity, age, feelings and states: I am a teacher. I am 30. I am hungry. I am fine.',
                        it: 'am / is / are per identità, età, sensazioni e stati: I am a teacher. I am 30. I am hungry. I am fine.',
                    },
                },
            ],
            italian: [
                {
                    name: 'Essere',
                    formula: {
                        en: 'Identity, origin, profession, characteristics and moods: sono insegnante, sono di Verona, sono felice.',
                        it: 'Identità, origine, professione, caratteristiche e stati d\'animo: sono insegnante, sono di Verona, sono felice.',
                    },
                },
                {
                    name: 'Avere',
                    formula: {
                        en: 'Age and physical states (hunger, thirst, cold, heat, sleepiness, fear): ho 30 anni, ho fame, ho freddo.',
                        it: 'Età e stati fisici (fame, sete, freddo, caldo, sonno, paura): ho 30 anni, ho fame, ho freddo.',
                    },
                },
                {
                    name: 'Stare',
                    formula: {
                        en: 'How you are, health and wellbeing: sto bene, come stai?',
                        it: 'Come stai, salute e benessere: sto bene, come stai?',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'I am a teacher.', tag: 'To be' },
                italian: { text: 'Sono insegnante.', tag: 'Essere' },
            },
            {
                english: { text: 'I am from Verona.', tag: 'To be' },
                italian: { text: 'Sono di Verona.', tag: 'Essere' },
            },
            {
                english: { text: 'I am 30 years old.', tag: 'To be' },
                italian: { text: 'Ho 30 anni.', tag: 'Avere' },
            },
            {
                english: { text: 'I am hungry.', tag: 'To be' },
                italian: { text: 'Ho fame.', tag: 'Avere' },
            },
            {
                english: { text: 'How are you?', tag: 'To be' },
                italian: { text: 'Come stai?', tag: 'Stare' },
            },
        ],
        notes: [
            {
                en: "English says 'I am' for all of these. Italian uses avere for age and physical states: 'Ho fame' literally means 'I have hunger'. Never say 'Sono fame' or 'Sono 30 anni'.",
                it: "L'inglese dice 'I am' per tutti questi casi. L'italiano usa avere per l'età e gli stati fisici: 'Ho fame' significa letteralmente 'I have hunger'. Non dire mai 'Sono fame' o 'Sono 30 anni'.",
            },
            {
                en: "For how you feel, health uses stare ('Sto bene') while moods use essere ('Sono felice', 'Sono contento/a').",
                it: "Per come ti senti, la salute usa stare ('Sto bene') mentre gli stati d'animo usano essere ('Sono felice', 'Sono contento/a').",
            },
        ],
    },
    {
        id: 'gender-plurals',
        level: 'A1',
        title: { en: 'Gender and plurals', it: 'Genere e plurale' },
        summary: {
            en: 'Masculine and feminine nouns, and how to make more than one.',
            it: 'Nomi maschili e femminili e come fare il plurale.',
        },
        forms: {
            english: [
                {
                    name: 'Plural with -s',
                    formula: {
                        en: 'Add -s (books), or -es after s, x, ch, sh (boxes). A few are irregular: man / men, child / children. Things have no gender: a table is just it.',
                        it: 'Aggiungi -s (books), oppure -es dopo s, x, ch, sh (boxes). Alcuni sono irregolari: man / men, child / children. Le cose non hanno genere: a table è semplicemente it.',
                    },
                },
            ],
            italian: [
                {
                    name: 'Genere',
                    formula: {
                        en: 'Every noun is masculine or feminine. Most nouns in -o are masculine, most in -a are feminine, and nouns in -e can be either: il libro, la casa, il nome, la notte.',
                        it: 'Ogni nome è maschile o femminile. La maggior parte dei nomi in -o è maschile, quella in -a è femminile e i nomi in -e possono essere l\'uno o l\'altro: il libro, la casa, il nome, la notte.',
                    },
                },
                {
                    name: 'Plurale',
                    formula: {
                        en: 'The ending changes: -o becomes -i (libro, libri), -a becomes -e (casa, case), -e becomes -i (notte, notti). Nouns ending in an accent do not change: caffè, città.',
                        it: 'La desinenza cambia: -o diventa -i (libro, libri), -a diventa -e (casa, case), -e diventa -i (notte, notti). I nomi che finiscono con un accento non cambiano: caffè, città.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'one book, two books', tag: 'Add -s' },
                italian: { text: 'un libro, due libri', tag: '-o becomes -i' },
            },
            {
                english: { text: 'one house, two houses', tag: 'Add -s' },
                italian: { text: 'una casa, due case', tag: '-a becomes -e' },
            },
            {
                english: { text: 'one night, two nights', tag: 'Add -s' },
                italian: { text: 'una notte, due notti', tag: '-e becomes -i' },
            },
            {
                english: { text: 'one man, two men', tag: 'Irregular plural' },
                italian: { text: 'un uomo, due uomini', tag: 'Irregular plural' },
            },
            {
                english: { text: 'one coffee, two coffees', tag: 'Add -s' },
                italian: { text: 'un caffè, due caffè', tag: 'No change (accent)' },
            },
        ],
        notes: [
            {
                en: 'Gender in Italian does not follow meaning, so always learn a noun with its article: il sole (the sun, masculine), la luna (the moon, feminine).',
                it: "In italiano il genere non segue il significato, quindi impara sempre il nome con il suo articolo: il sole (maschile), la luna (femminile).",
            },
            {
                en: 'Articles and adjectives must match the noun in gender and number. English never changes them.',
                it: "Articoli e aggettivi devono concordare con il nome in genere e numero. L'inglese non li cambia mai.",
            },
        ],
    },
    {
        id: 'adjectives',
        level: 'A1',
        title: { en: 'Describing things (adjectives)', it: 'Descrivere le cose (aggettivi)' },
        summary: {
            en: 'Where adjectives go and whether they change.',
            it: 'Dove vanno gli aggettivi e se cambiano.',
        },
        forms: {
            english: [
                {
                    name: 'Adjective before the noun',
                    formula: {
                        en: 'The adjective comes before the noun and never changes: a big house, big houses.',
                        it: "L'aggettivo viene prima del nome e non cambia mai: a big house, big houses.",
                    },
                },
            ],
            italian: [
                {
                    name: 'Adjective after the noun, matching it',
                    formula: {
                        en: 'Usually after the noun, matching its gender and number: una casa grande, case grandi, un libro nuovo, libri nuovi. Adjectives in -o have four forms (nuovo, nuova, nuovi, nuove), adjectives in -e have two (grande, grandi).',
                        it: 'Di solito dopo il nome, concorda con il suo genere e numero: una casa grande, case grandi, un libro nuovo, libri nuovi. Gli aggettivi in -o hanno quattro forme (nuovo, nuova, nuovi, nuove), quelli in -e ne hanno due (grande, grandi).',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'a big house', tag: 'Before the noun' },
                italian: { text: 'una casa grande', tag: 'After the noun' },
            },
            {
                english: { text: 'big houses', tag: 'No change' },
                italian: { text: 'case grandi', tag: 'Plural: grandi' },
            },
            {
                english: { text: 'a new book', tag: 'Before the noun' },
                italian: { text: 'un libro nuovo', tag: 'After the noun' },
            },
            {
                english: { text: 'a beautiful house', tag: 'Before the noun' },
                italian: { text: 'una bella casa', tag: 'Before the noun (common adjective)' },
            },
            {
                english: { text: 'She is tall.', tag: 'No change' },
                italian: { text: 'È alta.', tag: 'Matches the subject: alta' },
            },
        ],
        notes: [
            {
                en: 'English adjectives never change. Italian adjectives always match the noun: una ragazza alta, un ragazzo alto, ragazzi alti.',
                it: "Gli aggettivi inglesi non cambiano mai. Gli aggettivi italiani concordano sempre con il nome: una ragazza alta, un ragazzo alto, ragazzi alti.",
            },
            {
                en: 'A few common adjectives usually go before the noun (bello, buono, grande, piccolo, nuovo, vecchio). Colours and nationalities go after: una macchina rossa, un amico italiano.',
                it: 'Alcuni aggettivi comuni di solito vanno prima del nome (bello, buono, grande, piccolo, nuovo, vecchio). I colori e le nazionalità vanno dopo: una macchina rossa, un amico italiano.',
            },
            {
                en: "The position can change the meaning: 'un uomo povero' is a man without money, 'un povero uomo' is an unfortunate man.",
                it: "La posizione può cambiare il significato: 'un uomo povero' è un uomo senza soldi, 'un povero uomo' è un uomo sfortunato.",
            },
        ],
    },
    {
        id: 'possessives',
        level: 'A1',
        title: { en: 'Possessives (my, your)', it: 'Possessivi (mio, tuo)' },
        summary: {
            en: 'Saying who something belongs to.',
            it: 'Dire a chi appartiene qualcosa.',
        },
        forms: {
            english: [
                {
                    name: 'my, your, his, her, our, their',
                    formula: {
                        en: 'Placed before the noun. The form depends on the owner: his book, her book. It never changes with the thing owned.',
                        it: 'Si mette prima del nome. La forma dipende da chi possiede: his book, her book. Non cambia con la cosa posseduta.',
                    },
                },
            ],
            italian: [
                {
                    name: 'Article + mio, tuo, suo...',
                    formula: {
                        en: 'Article + possessive, matching the thing owned in gender and number: il mio libro, la mia casa, i miei libri, le mie case. Other forms: tuo, suo, nostro, vostro, loro.',
                        it: 'Articolo + possessivo, concorda con la cosa posseduta in genere e numero: il mio libro, la mia casa, i miei libri, le mie case. Altre forme: tuo, suo, nostro, vostro, loro.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'my book', tag: 'my' },
                italian: { text: 'il mio libro', tag: 'il mio (masculine)' },
            },
            {
                english: { text: 'my house', tag: 'my' },
                italian: { text: 'la mia casa', tag: 'la mia (feminine)' },
            },
            {
                english: { text: 'my mother', tag: 'my' },
                italian: { text: 'mia madre', tag: 'No article: singular family member' },
            },
            {
                english: { text: 'his house', tag: 'his' },
                italian: { text: 'la sua casa', tag: 'suo' },
            },
            {
                english: { text: 'her house', tag: 'her' },
                italian: { text: 'la sua casa', tag: 'suo' },
            },
            {
                english: { text: 'our friends', tag: 'our' },
                italian: { text: 'i nostri amici', tag: 'i nostri (masculine plural)' },
            },
        ],
        notes: [
            {
                en: "In English the possessive depends on the owner (his or her). In Italian it depends on the thing owned: 'la sua casa' can be his or her house. Context decides, or say 'la casa di lui / di lei'.",
                it: "In inglese il possessivo dipende da chi possiede (his o her). In italiano dipende dalla cosa posseduta: 'la sua casa' può essere la casa di lui o di lei. Decide il contesto, oppure dì 'la casa di lui / di lei'.",
            },
            {
                en: "Italian normally keeps the article before the possessive. It is dropped with singular family members (mia madre, tuo fratello), but not with plurals (i miei fratelli) and never with loro (la loro madre).",
                it: "L'italiano di solito mantiene l'articolo prima del possessivo. Si omette con i familiari al singolare (mia madre, tuo fratello), ma non con i plurali (i miei fratelli) e mai con loro (la loro madre).",
            },
        ],
    },
    {
        id: 'there-is',
        level: 'A1',
        title: { en: 'There is, there are', it: "C'è, ci sono" },
        summary: {
            en: 'Saying that something exists or is present.',
            it: 'Dire che qualcosa esiste o è presente.',
        },
        forms: {
            english: [
                {
                    name: 'There is / there are',
                    formula: {
                        en: 'there is + singular, there are + plural. Questions swap the order: Is there a bank?',
                        it: 'there is + singolare, there are + plurale. Nelle domande si inverte l\'ordine: Is there a bank?',
                    },
                },
            ],
            italian: [
                {
                    name: "C'è / ci sono",
                    formula: {
                        en: "c'è + singular, ci sono + plural. For a question, keep the same order and add ?: C'è una banca?",
                        it: "c'è + singolare, ci sono + plurale. Per fare una domanda mantieni lo stesso ordine e aggiungi ?: C'è una banca?",
                    },
                },
                {
                    name: 'Ecco',
                    formula: {
                        en: 'Used to point something out: Ecco la chiave! (Here is the key!)',
                        it: 'Si usa per indicare qualcosa: Ecco la chiave!',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'There is a bank near here.', tag: 'There is' },
                italian: { text: "C'è una banca qui vicino.", tag: "C'è" },
            },
            {
                english: { text: 'There are two banks in the square.', tag: 'There are' },
                italian: { text: 'Ci sono due banche in piazza.', tag: 'Ci sono' },
            },
            {
                english: { text: 'Is there a bus?', tag: 'Is there' },
                italian: { text: "C'è un autobus?", tag: "C'è + ?" },
            },
            {
                english: { text: "There isn't any milk.", tag: "There isn't" },
                italian: { text: "Non c'è latte.", tag: "Non c'è" },
            },
            {
                english: { text: 'Here is the key!', tag: 'Here is' },
                italian: { text: 'Ecco la chiave!', tag: 'Ecco' },
            },
        ],
        notes: [
            {
                en: "Do not confuse c'è (there is) with è (is). Use c'è to introduce something new ('C'è un libro sul tavolo') and è to say where something you already know is ('Il libro è sul tavolo').",
                it: "Non confondere c'è con è. Usa c'è per introdurre qualcosa di nuovo ('C'è un libro sul tavolo') ed è per dire dove si trova qualcosa che già conosci ('Il libro è sul tavolo').",
            },
            {
                en: "In a negative sentence Italian just adds non: 'Non c'è latte'. English often adds any: 'There isn't any milk'.",
                it: "In una frase negativa l'italiano aggiunge solo non: 'Non c'è latte'. L'inglese spesso aggiunge any: 'There isn't any milk'.",
            },
        ],
    },
    {
        id: 'liking',
        level: 'A1',
        title: { en: 'Liking things', it: 'Dire che ti piace qualcosa' },
        summary: {
            en: 'English and Italian turn the sentence around.',
            it: "L'inglese e l'italiano costruiscono la frase in modo opposto.",
        },
        forms: {
            english: [
                {
                    name: 'Like',
                    formula: {
                        en: 'Person + like(s) + thing. The person who likes is the subject: I like coffee. She likes Rome.',
                        it: 'Persona + like(s) + cosa. Chi apprezza è il soggetto: I like coffee. She likes Rome.',
                    },
                },
            ],
            italian: [
                {
                    name: 'Piacere',
                    formula: {
                        en: 'The thing you like is the subject, and the person is a pronoun (mi, ti, gli / le, ci, vi, gli). It means "is pleasing to": Mi piace il caffè.',
                        it: 'La cosa che ti piace è il soggetto e la persona è un pronome (mi, ti, gli / le, ci, vi, gli). Vuol dire "è gradito a": Mi piace il caffè.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'I like coffee.', tag: 'Person + like' },
                italian: { text: 'Mi piace il caffè.', tag: 'Mi piace (one thing)' },
            },
            {
                english: { text: 'I like the books.', tag: 'Person + like' },
                italian: { text: 'Mi piacciono i libri.', tag: 'Mi piacciono (more than one)' },
            },
            {
                english: { text: 'Do you like pizza?', tag: 'Do + person + like' },
                italian: { text: 'Ti piace la pizza?', tag: 'Ti piace' },
            },
            {
                english: { text: 'She likes Rome.', tag: 'Person + likes' },
                italian: { text: 'Le piace Roma.', tag: 'Le piace' },
            },
            {
                english: { text: 'We like to dance.', tag: 'Person + like + to' },
                italian: { text: 'Ci piace ballare.', tag: 'Ci piace + infinitive' },
            },
        ],
        notes: [
            {
                en: "The word order is reversed. In English you are the subject. In Italian the thing you like is the subject, and you are the indirect object: 'Mi piace il caffè' is 'Coffee is pleasing to me'.",
                it: "L'ordine è invertito. In inglese il soggetto sei tu. In italiano il soggetto è la cosa che ti piace e tu sei il complemento di termine: 'Mi piace il caffè' è 'Coffee is pleasing to me'.",
            },
            {
                en: "Piacere agrees with what you like, not with who likes it: 'mi piace' for one thing or an action, 'mi piacciono' for more than one.",
                it: "Piacere concorda con ciò che ti piace, non con chi lo apprezza: 'mi piace' per una cosa o un'azione, 'mi piacciono' per più cose.",
            },
            {
                en: "With a name or a stressed pronoun, add a: 'A Maria piace il caffè', 'A me piace il caffè'.",
                it: "Con un nome o un pronome tonico, aggiungi a: 'A Maria piace il caffè', 'A me piace il caffè'.",
            },
        ],
    },
    {
        id: 'past-connected',
        level: 'A2',
        title: { en: 'Past connected to now', it: 'Passato legato al presente' },
        summary: {
            en: 'Things that began in the past and continue, experiences, and recent news.',
            it: 'Cose iniziate nel passato che continuano, esperienze e notizie recenti.',
        },
        forms: {
            english: [
                {
                    name: 'Present perfect',
                    formula: {
                        en: 'have / has + past participle: I have lived here for five years. Used for ongoing situations, life experiences and recent actions.',
                        it: 'have / has + participio passato: I have lived here for five years. Si usa per situazioni che continuano, esperienze di vita e azioni recenti.',
                    },
                },
            ],
            italian: [
                {
                    name: 'Presente + da',
                    formula: {
                        en: 'For something that started in the past and still continues, use the plain presente with da: Vivo qui da cinque anni.',
                        it: 'Per qualcosa che è iniziato nel passato e continua ancora, usa il presente semplice con da: Vivo qui da cinque anni.',
                    },
                },
                {
                    name: 'Passato prossimo',
                    formula: {
                        en: 'For completed actions, experiences and recent news: Ho appena mangiato. Hai mai mangiato il tiramisù?',
                        it: 'Per azioni concluse, esperienze e notizie recenti: Ho appena mangiato. Hai mai mangiato il tiramisù?',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'I have lived here for five years.', tag: 'Present perfect' },
                italian: { text: 'Vivo qui da cinque anni.', tag: 'Presente + da' },
            },
            {
                english: { text: 'I have known her for a long time.', tag: 'Present perfect' },
                italian: { text: 'La conosco da molto tempo.', tag: 'Presente + da' },
            },
            {
                english: { text: 'I have just eaten.', tag: 'Present perfect' },
                italian: { text: 'Ho appena mangiato.', tag: 'Passato prossimo' },
            },
            {
                english: { text: 'Have you ever eaten tiramisu?', tag: 'Present perfect' },
                italian: { text: 'Hai mai mangiato il tiramisù?', tag: 'Passato prossimo' },
            },
            {
                english: { text: 'She has already left.', tag: 'Present perfect' },
                italian: { text: 'È già partita.', tag: 'Passato prossimo' },
            },
        ],
        notes: [
            {
                en: "For something still going on, Italian uses the presente + da, never the passato prossimo: 'Vivo qui da cinque anni', not 'Ho vissuto qui da cinque anni'.",
                it: "Per qualcosa che continua ancora, l'italiano usa il presente + da, mai il passato prossimo: 'Vivo qui da cinque anni', non 'Ho vissuto qui da cinque anni'.",
            },
            {
                en: "The English difference between 'I ate' and 'I have eaten' often disappears in Italian: both are 'Ho mangiato'. Time words such as ieri or appena make the meaning clear.",
                it: "La differenza inglese tra 'I ate' e 'I have eaten' spesso scompare in italiano: entrambi sono 'Ho mangiato'. Parole di tempo come ieri o appena chiariscono il significato.",
            },
        ],
    },
    {
        id: 'modals',
        level: 'A2',
        title: { en: 'Can, must, want', it: 'Potere, dovere, volere' },
        summary: {
            en: 'Ability, obligation and wishes.',
            it: 'Capacità, obbligo e desideri.',
        },
        forms: {
            english: [
                {
                    name: 'Modal verbs',
                    formula: {
                        en: 'can / must / should + base verb, with no -s and no to: she can swim. Want and have to need to + base verb: I want to eat, I have to work.',
                        it: 'can / must / should + verbo base, senza -s e senza to: she can swim. Want e have to vogliono to + verbo base: I want to eat, I have to work.',
                    },
                },
            ],
            italian: [
                {
                    name: 'Potere, dovere, volere + infinito',
                    formula: {
                        en: 'Conjugated like normal verbs, followed by the infinitive with no word for to: posso nuotare, devo lavorare, voglio mangiare.',
                        it: "Si coniugano come verbi normali e sono seguiti dall'infinito senza nessuna parola per to: posso nuotare, devo lavorare, voglio mangiare.",
                    },
                },
                {
                    name: 'Sapere + infinito',
                    formula: {
                        en: "To say you can because you know how: So nuotare ('I can swim').",
                        it: "Per dire che sai fare qualcosa: So nuotare ('I can swim').",
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'I can work tomorrow.', tag: 'can + base verb' },
                italian: { text: 'Domani posso lavorare.', tag: 'Potere + infinitive' },
            },
            {
                english: { text: 'I can swim.', tag: 'can (ability)' },
                italian: { text: 'So nuotare.', tag: 'Sapere + infinitive' },
            },
            {
                english: { text: 'I have to work.', tag: 'have to + base verb' },
                italian: { text: 'Devo lavorare.', tag: 'Dovere + infinitive' },
            },
            {
                english: { text: 'She wants to eat.', tag: 'want to + base verb' },
                italian: { text: 'Vuole mangiare.', tag: 'Volere + infinitive' },
            },
            {
                english: { text: 'Can you help me?', tag: 'can + subject' },
                italian: { text: 'Puoi aiutarmi?', tag: 'Potere + infinitive' },
            },
        ],
        notes: [
            {
                en: "English modals never change (she can, he must), and want needs to: 'I want to eat'. Italian modals have a full set of endings (posso, puoi, può...) and use no word for to: 'Voglio mangiare'.",
                it: "I modali inglesi non cambiano mai (she can, he must) e want richiede to: 'I want to eat'. I modali italiani hanno tutte le desinenze (posso, puoi, può...) e non usano nessuna parola per to: 'Voglio mangiare'.",
            },
            {
                en: "For ability, Italian often uses sapere, not potere: 'So nuotare'. Potere is for permission or possibility: 'Posso entrare?'",
                it: "Per la capacità, l'italiano usa spesso sapere, non potere: 'So nuotare'. Potere si usa per il permesso o la possibilità: 'Posso entrare?'",
            },
        ],
    },
    {
        id: 'commands',
        level: 'A2',
        title: { en: 'Giving orders and advice', it: 'Dare ordini e consigli' },
        summary: {
            en: 'Telling someone to do something, or not to.',
            it: 'Dire a qualcuno di fare qualcosa, o di non farla.',
        },
        forms: {
            english: [
                {
                    name: 'Imperative',
                    formula: {
                        en: "The base verb, the same for everyone: Speak slowly! Add please to be polite. Do not + verb for no: Don't speak! Let's + verb for we: Let's go!",
                        it: "Il verbo base, uguale per tutti: Speak slowly! Aggiungi please per essere gentile. Do not + verbo per dire no: Don't speak! Let's + verbo per noi: Let's go!",
                    },
                },
            ],
            italian: [
                {
                    name: 'Imperativo',
                    formula: {
                        en: 'Different forms for tu, noi, voi and Lei (formal). For -are verbs: Parla! (tu), Parliamo! (noi), Parlate! (voi), Parli! (Lei).',
                        it: 'Forme diverse per tu, noi, voi e Lei (formale). Per i verbi in -are: Parla! (tu), Parliamo! (noi), Parlate! (voi), Parli! (Lei).',
                    },
                },
                {
                    name: 'Non + infinito',
                    formula: {
                        en: 'To tell one person (tu) not to do something: non + infinitive: Non parlare!',
                        it: 'Per dire a una persona (tu) di non fare qualcosa: non + infinito: Non parlare!',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'Speak slowly!', tag: 'Imperative' },
                italian: { text: 'Parla lentamente!', tag: 'Imperativo (tu)' },
            },
            {
                english: { text: 'Speak slowly, please! (to several people)', tag: 'Imperative' },
                italian: { text: 'Parlate lentamente, per favore!', tag: 'Imperativo (voi)' },
            },
            {
                english: { text: "Let's go!", tag: "Let's + base verb" },
                italian: { text: 'Andiamo!', tag: 'Imperativo (noi)' },
            },
            {
                english: { text: "Don't speak!", tag: 'Do not + base verb' },
                italian: { text: 'Non parlare!', tag: 'Non + infinitive (tu)' },
            },
            {
                english: { text: 'Please sit down.', tag: 'Imperative + please' },
                italian: { text: 'Si accomodi, prego.', tag: 'Imperativo (Lei)' },
            },
        ],
        notes: [
            {
                en: 'English commands are the same for everyone. In Italian you have to choose between tu, voi and Lei (formal, for strangers and older people). Using tu with a stranger can sound rude.',
                it: "In inglese i comandi sono uguali per tutti. In italiano devi scegliere tra tu, voi e Lei (formale, per sconosciuti e persone più anziane). Dare del tu a uno sconosciuto può sembrare scortese.",
            },
            {
                en: "The non + infinitive rule is only for tu. For noi and voi, use the normal command form: 'Non parliamo!', 'Non parlate!'",
                it: "La regola non + infinito vale solo per tu. Per noi e voi si usa la forma normale del comando: 'Non parliamo!', 'Non parlate!'",
            },
        ],
    },
    {
        id: 'comparing',
        level: 'A2',
        title: { en: 'Comparing things', it: 'Confrontare le cose' },
        summary: {
            en: 'Bigger than, the best, as good as.',
            it: 'Più grande di, il migliore, buono come.',
        },
        forms: {
            english: [
                {
                    name: 'Comparative and superlative',
                    formula: {
                        en: 'Short adjectives add -er / -est (taller, the tallest). Long ones use more / the most (more interesting, the most interesting). Than introduces the second thing. Irregular: good, better, best.',
                        it: "Gli aggettivi corti aggiungono -er / -est (taller, the tallest). Quelli lunghi usano more / the most (more interesting, the most interesting). Than introduce il secondo termine. Irregolari: good, better, best.",
                    },
                },
            ],
            italian: [
                {
                    name: 'Più / meno ... di',
                    formula: {
                        en: 'più (more) or meno (less) + adjective + di before a noun or pronoun: più alto di Marco. The adjective never changes its form, except to match the noun.',
                        it: "più o meno + aggettivo + di davanti a un nome o a un pronome: più alto di Marco. L'aggettivo cambia solo per concordare con il nome.",
                    },
                },
                {
                    name: 'Il / la più ...',
                    formula: {
                        en: "Superlative: article + più + adjective: la città più bella. Equality: come (buona come la tua). Irregular: buono, migliore.",
                        it: "Superlativo: articolo + più + aggettivo: la città più bella. Uguaglianza: come (buona come la tua). Irregolare: buono, migliore.",
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'She is taller than Marco.', tag: 'Adjective + -er + than' },
                italian: { text: 'È più alta di Marco.', tag: 'Più + adjective + di' },
            },
            {
                english: { text: 'This book is more interesting than that one.', tag: 'More + adjective + than' },
                italian: { text: 'Questo libro è più interessante di quello.', tag: 'Più + adjective + di' },
            },
            {
                english: { text: 'It is the most beautiful city in Italy.', tag: 'The most + adjective' },
                italian: { text: "È la città più bella d'Italia.", tag: 'La più + adjective' },
            },
            {
                english: { text: 'My pizza is as good as yours.', tag: 'as ... as' },
                italian: { text: 'La mia pizza è buona come la tua.', tag: 'Come' },
            },
            {
                english: { text: 'This wine is better than that one.', tag: 'Irregular: better' },
                italian: { text: 'Questo vino è migliore di quello.', tag: 'Irregular: migliore' },
            },
        ],
        notes: [
            {
                en: 'English changes short adjectives (taller) and adds more to long ones (more interesting). Italian always uses più or meno and keeps the adjective as it is: più alto, più interessante.',
                it: "L'inglese cambia gli aggettivi corti (taller) e aggiunge more a quelli lunghi (more interesting). L'italiano usa sempre più o meno e lascia l'aggettivo com'è: più alto, più interessante.",
            },
            {
                en: "Italian has two words for than. Use di before a noun or pronoun (più alto di me). Use che to compare two adjectives or two verbs: 'È più simpatica che intelligente'.",
                it: "L'italiano ha due parole per than. Usa di davanti a un nome o a un pronome (più alto di me). Usa che per confrontare due aggettivi o due verbi: 'È più simpatica che intelligente'.",
            },
        ],
    },
];

// The order the ideas appear in the app: A1 first, then A2.
const IDEA_ORDER = [
    'be-have-feel',
    'articles',
    'gender-plurals',
    'adjectives',
    'possessives',
    'habits',
    'now',
    'negation',
    'questions',
    'there-is',
    'liking',
    'past-finished',
    'past-ongoing',
    'past-connected',
    'future',
    'modals',
    'commands',
    'comparing',
];

// Ideas from later batches are added after the ones above, then everything is sorted by level
// (A1, A2, B1...). The sort keeps the order inside each level.
const EXTRA_IDEAS = [...BATCH_3, ...BATCH_4, ...BATCH_5];

// French is added on top of the English and Italian ideas (the content is in grammarCompareFrenchA1.js,
// and later files for A2 and B1 are added to FRENCH). For each idea it adds the French title and
// summary, French versions of the English and Italian explanations, the French forms and examples,
// and the notes for the pairs that include French.
const FRENCH = { ...FR_A1, ...FR_A2A, ...FR_A2B, ...FR_A2C, ...FR_B1A, ...FR_B1B, ...FR_B1C, ...FR_B1D };

const at = (list, i) => (list && list[i]) || null;

const withFrench = (idea) => {
    const fr = FRENCH[idea.id];
    if (!fr) return idea;

    const translateForms = (lang) => idea.forms[lang].map((form, i) => ({
        ...form,
        name: at(fr.names[lang], i) ? { en: form.name, fr: at(fr.names[lang], i) } : form.name,
        formula: { ...form.formula, fr: fr.formulas[lang][i] },
    }));

    const translateExample = (example, i) => {
        const translateSide = (lang) => {
            const frTag = at(fr.tags[lang], i);
            return { ...example[lang], tag: frTag ? { en: example[lang].tag, fr: frTag } : example[lang].tag };
        };
        return {
            ...example,
            english: translateSide('english'),
            italian: translateSide('italian'),
            french: fr.examples[i],
        };
    };

    return {
        ...idea,
        title: { ...idea.title, fr: fr.title },
        summary: { ...idea.summary, fr: fr.summary },
        pairSummary: fr.pairSummary,
        forms: {
            ...idea.forms,
            english: translateForms('english'),
            italian: translateForms('italian'),
            french: fr.forms,
        },
        examples: idea.examples.map(translateExample),
        notes: [
            ...idea.notes.map((note, i) => ({ ...note, fr: fr.notes[i] })),
            ...fr.pairNotes,
        ],
    };
};

// Spanish is added on top of the English, Italian and French ideas, the same way. For each idea it
// adds the Spanish title and summary, Spanish versions of every existing explanation and note,
// the Spanish forms and examples, and the notes for the pairs that include Spanish.
const SPANISH = { ...ES_A1A, ...ES_A1B, ...ES_A1C, ...ES_A1D, ...ES_A1E, ...ES_A2A, ...ES_A2B, ...ES_A2C, ...ES_A2D, ...ES_A2E, ...ES_A2F, ...ES_B1A, ...ES_B1B, ...ES_B1C, ...ES_B1D, ...ES_B1E, ...ES_B1F };

// Adds a Spanish version to a value that is plain text or text in several languages
const addEs = (value, es) => {
    if (!es) return value;
    if (typeof value === 'string') return { en: value, it: value, fr: value, es };
    return { ...value, es };
};

const withSpanish = (idea) => {
    const es = SPANISH[idea.id];
    if (!es) return idea;

    const translateForms = (lang) => idea.forms[lang].map((form, i) => ({
        ...form,
        name: addEs(form.name, at(es.names[lang], i)),
        formula: { ...form.formula, es: es.formulas[lang][i] },
    }));

    const translateExample = (example, i) => {
        const translateSide = (lang) => ({
            ...example[lang],
            tag: addEs(example[lang].tag, at(es.tags[lang], i)),
        });
        return {
            ...example,
            english: translateSide('english'),
            italian: translateSide('italian'),
            french: translateSide('french'),
            spanish: es.examples[i],
        };
    };

    const pairSummary = { ...(idea.pairSummary || {}) };
    Object.keys(es.pairSummaryEs || {}).forEach((key) => {
        pairSummary[key] = { ...pairSummary[key], es: es.pairSummaryEs[key] };
    });
    Object.assign(pairSummary, es.pairSummary || {});

    return {
        ...idea,
        title: { ...idea.title, es: es.title },
        summary: { ...idea.summary, es: es.summary },
        pairSummary,
        forms: {
            ...idea.forms,
            english: translateForms('english'),
            italian: translateForms('italian'),
            french: translateForms('french'),
            spanish: es.forms,
        },
        examples: idea.examples.map(translateExample),
        notes: [
            ...idea.notes.map((note, i) => ({ ...note, es: es.notes[i] })),
            ...es.pairNotes,
        ],
    };
};

// Portuguese is added on top of the English, Italian, French and Spanish ideas, the same way. For each idea
// it adds the Portuguese title and summary, Portuguese versions of every existing explanation and note,
// the Portuguese forms and examples, and the notes for the pairs that include Portuguese.
const PORTUGUESE = { ...PT_A1A, ...PT_A1B, ...PT_A1C, ...PT_A1D, ...PT_A1E, ...PT_A2A, ...PT_A2B, ...PT_A2C, ...PT_A2D, ...PT_A2E, ...PT_A2F, ...PT_B1A, ...PT_B1B, ...PT_B1C, ...PT_B1D };

// Adds a Portuguese version to a value that is plain text or text in several languages
const addPt = (value, pt) => {
    if (!pt) return value;
    if (typeof value === 'string') return { en: value, it: value, fr: value, es: value, pt };
    return { ...value, pt };
};

const withPortuguese = (idea) => {
    const pt = PORTUGUESE[idea.id];
    if (!pt) return idea;

    const translateForms = (lang) => idea.forms[lang].map((form, i) => ({
        ...form,
        name: addPt(form.name, at(pt.names[lang], i)),
        formula: { ...form.formula, pt: pt.formulas[lang][i] },
    }));

    const translateExample = (example, i) => {
        const translateSide = (lang) => ({
            ...example[lang],
            tag: addPt(example[lang].tag, at(pt.tags[lang], i)),
        });
        return {
            ...example,
            english: translateSide('english'),
            italian: translateSide('italian'),
            french: translateSide('french'),
            spanish: translateSide('spanish'),
            portuguese: pt.examples[i],
        };
    };

    const pairSummary = { ...(idea.pairSummary || {}) };
    Object.keys(pt.pairSummaryPt || {}).forEach((key) => {
        pairSummary[key] = { ...pairSummary[key], pt: pt.pairSummaryPt[key] };
    });
    Object.assign(pairSummary, pt.pairSummary || {});

    return {
        ...idea,
        title: { ...idea.title, pt: pt.title },
        summary: { ...idea.summary, pt: pt.summary },
        pairSummary,
        forms: {
            ...idea.forms,
            english: translateForms('english'),
            italian: translateForms('italian'),
            french: translateForms('french'),
            spanish: translateForms('spanish'),
            portuguese: pt.forms,
        },
        examples: idea.examples.map(translateExample),
        notes: [
            ...idea.notes.map((note, i) => ({ ...note, pt: pt.notes[i] })),
            ...pt.pairNotes,
        ],
    };
};

export const COMPARE_IDEAS = [
    ...IDEA_ORDER.map(id => ALL_IDEAS.find(idea => idea.id === id)),
    ...EXTRA_IDEAS,
].sort((a, b) => a.level.localeCompare(b.level)).map(withFrench).map(withSpanish).map(withPortuguese);